/**
 * Navigation Bar Component
 *
 * Top navigation bar with logo, company name, and optional links.
 * Responsive: Full bar on desktop, hamburger menu on mobile.
 */

export class NavBar {
  constructor(container, config = {}) {
    this.container = container;
    this.config = config;
    this.isMobileMenuOpen = false;
  }

  /**
   * Update configuration from backend
   */
  updateConfig(backendConfig) {
    if (backendConfig.company?.name) {
      this.config.companyName = backendConfig.company.name;
    }
    if (backendConfig.settings?.header_name) {
      this.config.headerName = backendConfig.settings.header_name;
    }
    // Refresh navbar if already rendered
    if (this.navElement) {
      this.render();
    }
  }

  /**
   * Render navigation bar
   */
  render() {
    // Clear existing content
    this.container.innerHTML = '';

    // Create navbar element
    this.navElement = document.createElement('nav');
    this.navElement.className = 'chat-navbar';

    // Left section: Logo only (company name removed per client request)
    const leftSection = document.createElement('div');
    leftSection.className = 'chat-navbar__left';

    // Company logo (not bot avatar)
    if (this.config.companyLogo || this.config.botAvatar) {
      const logo = document.createElement('img');
      logo.src = this.config.companyLogo || this.config.botAvatar;  // Use companyLogo if available, fallback to botAvatar
      logo.alt = 'Vilniaus Turtas';
      logo.className = 'chat-navbar__logo';
      leftSection.appendChild(logo);
    }

    this.navElement.appendChild(leftSection);

    // Right section: Navigation links (optional)
    if (this.config.navLinks && this.config.navLinks.length > 0) {
      const rightSection = document.createElement('div');
      rightSection.className = 'chat-navbar__right';

      // Desktop links
      const linksContainer = document.createElement('div');
      linksContainer.className = 'chat-navbar__links';

      this.config.navLinks.forEach(link => {
        const linkEl = document.createElement('a');
        linkEl.href = link.url;
        linkEl.textContent = link.label;
        linkEl.className = 'chat-navbar__link';
        linkEl.target = '_blank';
        linkEl.rel = 'noopener noreferrer';
        linksContainer.appendChild(linkEl);
      });

      rightSection.appendChild(linksContainer);

      // Mobile hamburger menu
      const hamburger = document.createElement('button');
      hamburger.className = 'chat-navbar__hamburger';
      hamburger.setAttribute('aria-label', 'Toggle menu');
      hamburger.innerHTML = `
        <span></span>
        <span></span>
        <span></span>
      `;
      hamburger.addEventListener('click', () => this.toggleMobileMenu());
      rightSection.appendChild(hamburger);

      this.navElement.appendChild(rightSection);

      // Mobile menu dropdown
      const mobileMenu = document.createElement('div');
      mobileMenu.className = 'chat-navbar__mobile-menu';

      this.config.navLinks.forEach(link => {
        const linkEl = document.createElement('a');
        linkEl.href = link.url;
        linkEl.textContent = link.label;
        linkEl.className = 'chat-navbar__mobile-link';
        linkEl.target = '_blank';
        linkEl.rel = 'noopener noreferrer';
        linkEl.addEventListener('click', () => this.toggleMobileMenu());
        mobileMenu.appendChild(linkEl);
      });

      this.navElement.appendChild(mobileMenu);
    }

    this.container.appendChild(this.navElement);
  }

  /**
   * Toggle mobile menu
   */
  toggleMobileMenu() {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;

    const mobileMenu = this.navElement.querySelector('.chat-navbar__mobile-menu');
    const hamburger = this.navElement.querySelector('.chat-navbar__hamburger');

    if (this.isMobileMenuOpen) {
      mobileMenu.classList.add('chat-navbar__mobile-menu--open');
      hamburger.classList.add('chat-navbar__hamburger--open');
    } else {
      mobileMenu.classList.remove('chat-navbar__mobile-menu--open');
      hamburger.classList.remove('chat-navbar__hamburger--open');
    }
  }

  /**
   * Close mobile menu (call from outside)
   */
  closeMobileMenu() {
    if (this.isMobileMenuOpen) {
      this.toggleMobileMenu();
    }
  }
}
